var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AccountType } from '../model/Enums.js';
import { convertInMatrix } from '../utils.js';
/**
 * A AccountsDataTableBuilder is used to setup and build two-dimensional arrays containing accounts.
 *
 * @public
 */
export class AccountsDataTableBuilder {
    constructor(accounts) {
        this.accounts = accounts;
        this.shouldIncludeArchived = false;
        this.shouldAddGroups = false;
        this.shouldAddProperties = false;
        this.shouldAddIds = false;
        this.shouldAddHiddenProperties = false;
        this.propertyKeys = [];
    }
    /**
     * Defines whether the archived accounts should be included.
     *
     * @param include - Whether to include archived accounts
     *
     * @returns This builder with respective include archived option, for chaining.
     */
    archived(include) {
        this.shouldIncludeArchived = include;
        return this;
    }
    /**
     * Defines whether include account groups.
     *
     * @param include - Whether to include groups
     *
     * @returns This builder with respective include groups option, for chaining.
     */
    groups(include) {
        this.shouldAddGroups = include;
        return this;
    }
    /**
     * Defines whether include custom account properties.
     *
     * @param include - Whether to include properties
     *
     * @returns This builder with respective include properties option, for chaining.
     */
    properties(include) {
        this.shouldAddProperties = include;
        return this;
    }
    /**
     * Defines whether include account ids.
     *
     * @param include - Whether to include ids
     *
     * @returns This builder with respective include ids option, for chaining.
     */
    ids(include) {
        this.shouldAddIds = include;
        return this;
    }
    /**
     * Defines whether to include hidden properties (keys ending with underscore "_").
     * Only relevant when {@link properties} is enabled.
     * Default is false — hidden properties are excluded.
     *
     * @param include - Whether to include hidden properties
     *
     * @returns This builder with respective option, for chaining.
     */
    hiddenProperties(include) {
        this.shouldAddHiddenProperties = include;
        return this;
    }
    getPropertyKeys() {
        if (this.propertyKeys.length === 0) {
            for (const account of this.accounts) {
                for (const key of account.getPropertyKeys()) {
                    if (!this.shouldAddHiddenProperties && key.endsWith('_')) {
                        continue;
                    }
                    if (this.propertyKeys.indexOf(key) <= -1) {
                        this.propertyKeys.push(key);
                    }
                }
            }
            this.propertyKeys = this.propertyKeys.sort();
        }
        return this.propertyKeys;
    }
    getTypeIndex(type) {
        if (type === AccountType.ASSET) {
            return 0;
        }
        if (type === AccountType.LIABILITY) {
            return 1;
        }
        if (type === AccountType.INCOMING) {
            return 2;
        }
        return 3;
    }
    getMaxNumberOfGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            let maxNumberOfGroups = 0;
            for (const account of this.accounts) {
                const groups = yield account.getGroups();
                if (groups.length > maxNumberOfGroups) {
                    maxNumberOfGroups = groups.length;
                }
            }
            return maxNumberOfGroups;
        });
    }
    /**
     * Sorts groups for an account in hierarchy-path order:
     * 1. Hierarchical groups (those with parent or children) come first, ordered by:
     *    - Root group name (alphabetically)
     *    - Depth within the hierarchy (parent before child)
     * 2. Free groups (no parent and no children) come last, sorted alphabetically
     */
    sortGroupsHierarchyPath_(groups) {
        // Partition into hierarchical vs free groups
        const hierarchicalGroups = [];
        const freeGroups = [];
        for (const group of groups) {
            if (group.getParent() != null || group.hasChildren()) {
                hierarchicalGroups.push(group);
            }
            else {
                freeGroups.push(group);
            }
        }
        // Group hierarchical groups by their root
        const byRoot = new Map();
        for (const group of hierarchicalGroups) {
            const root = group.getRoot();
            const rootId = root.getId() || '';
            if (!byRoot.has(rootId)) {
                byRoot.set(rootId, []);
            }
            byRoot.get(rootId).push(group);
        }
        // Sort chains: first by root name alphabetically, then by depth within each chain
        const sortedChains = Array.from(byRoot.entries())
            .sort((a, b) => {
            const rootA = a[1][0].getRoot();
            const rootB = b[1][0].getRoot();
            return rootA.getNormalizedName().localeCompare(rootB.getNormalizedName());
        })
            .map(([_, chainGroups]) => chainGroups.sort((a, b) => a.getDepth() - b.getDepth()));
        // Sort free groups alphabetically
        freeGroups.sort((a, b) => a.getNormalizedName().localeCompare(b.getNormalizedName()));
        // Combine: hierarchical chains first, then free groups
        const result = [];
        for (const chain of sortedChains) {
            result.push(...chain);
        }
        result.push(...freeGroups);
        return result;
    }
    /**
     * Builds a two-dimensional array containing all accounts.
     *
     * @returns A promise resolving to a two-dimensional array containing all accounts
     */
    build() {
        return __awaiter(this, void 0, void 0, function* () {
            let table = new Array();
            let accounts = this.accounts;
            if (!this.shouldIncludeArchived) {
                accounts = this.accounts.filter(a => !a.isArchived());
            }
            let headers = [];
            if (this.shouldAddIds) {
                headers.push('Account Id');
            }
            headers.push('Name');
            headers.push('Type');
            if (this.shouldAddGroups) {
                const maxGroups = yield this.getMaxNumberOfGroups();
                for (let i = 0; i < maxGroups; i++) {
                    headers.push('Group');
                }
            }
            accounts.sort((a1, a2) => {
                let ret = this.getTypeIndex(a1.getType()) - this.getTypeIndex(a2.getType());
                if (ret === 0) {
                    ret = a1.getNormalizedName().localeCompare(a2.getNormalizedName());
                }
                return ret;
            });
            let propertyKeys = [];
            if (this.shouldAddProperties) {
                propertyKeys = this.getPropertyKeys();
            }
            for (const account of accounts) {
                const line = [];
                if (this.shouldAddIds) {
                    line.push(account.getId());
                }
                line.push(account.getName());
                line.push(account.getType());
                if (this.shouldAddGroups) {
                    const groups = this.sortGroupsHierarchyPath_(yield account.getGroups());
                    for (const group of groups) {
                        line.push(group.getName());
                    }
                }
                if (this.shouldAddGroups && this.shouldAddProperties) {
                    const numOfBlankCells = headers.length - line.length;
                    for (let i = 0; i < numOfBlankCells; i++) {
                        line.push('');
                    }
                }
                if (this.shouldAddProperties) {
                    const properties = account.getProperties();
                    for (const key of propertyKeys) {
                        const propertyValue = properties[key];
                        if (propertyValue) {
                            line.push(propertyValue);
                            continue;
                        }
                        line.push('');
                    }
                }
                table.push(line);
            }
            if (this.shouldAddProperties) {
                headers = headers.concat(propertyKeys);
            }
            table.unshift(headers);
            convertInMatrix(table);
            return table;
        });
    }
}
//# sourceMappingURL=AccountsDataTableBuilder.js.map